"use client";

import React, { useState, useMemo, useEffect } from "react";
import { PlusIcon, TrashIcon, Search, Pencil, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/shadcn-components/ui/card";
import { Alert, AlertDescription } from "@/shadcn-components/ui/alert";
import { Badge } from "@/shadcn-components/ui/badge";
import { Button } from "@/shadcn-components/ui/button";
import { Input } from "@/shadcn-components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/shadcn-components/ui/sheet";
import { useAccountsStore } from "@/lib/accounts/store";
import type { TradingAccount } from "@/lib/accounts/types";
import { gatewayLogoUrl } from "@/lib/accounts/brokerLogo";
import {
  useSyncedAccountsStore,
  selectUnmatchedSynced,
  type SyncedAccountView,
} from "@/lib/accounts/syncedAccounts";
import { useConnectionProgressStore } from "@/vybe-trader-client/connection-progress/stores/useConnectionProgressStore";
import { BrokerLogo, VybeLogo } from "@/components/common";
import {
  AccountDetailsScreen,
  AddAccountContent,
  EditAccountContent,
} from "@/components/account-information";

interface AccountsSheetProps {
  trigger: React.ReactNode;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

type ViewState = "accounts" | "add-account" | "account-details" | "edit-account";

/**
 * A row in the merged accounts list: either an account saved on this device, or one the signed-in user's
 * Vybe Trader profile owns that they have not added here. Synced rows are read-only — their name, server
 * and credentials are managed in the client portal, so this panel offers no edit or delete for them.
 */
type AccountRow =
  | { kind: "local"; account: TradingAccount }
  | { kind: "synced"; synced: SyncedAccountView };

export function AccountsSheet({ trigger, isOpen, onOpenChange }: AccountsSheetProps) {
  const [currentView, setCurrentView] = useState<ViewState>("accounts");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [editAccountId, setEditAccountId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Get accounts data
  const accounts = useAccountsStore((state) => state.accounts);
  const activeAccountId = useAccountsStore((state) => state.activeAccountId);
  const setActiveAccountId = useAccountsStore((state) => state.setActiveAccountId);
  const deleteAccount = useAccountsStore((state) => state.deleteAccount);

  // Get connection status
  const isConnected = useConnectionProgressStore((state) => state.isConnected);

  // Internal account sync: the accounts this user's Vybe Trader profile owns (the same ones the client
  // portal lists). Present only while signed in — signed out there is nothing here to show.
  const syncedItems = useSyncedAccountsStore((state) => state.items);
  const syncedStatus = useSyncedAccountsStore((state) => state.status);
  const syncedError = useSyncedAccountsStore((state) => state.error);
  const connectingLogin = useSyncedAccountsStore((state) => state.connectingLogin);
  const connectSynced = useSyncedAccountsStore((state) => state.connect);
  const refreshSynced = useSyncedAccountsStore((state) => state.refresh);

  // Opening the panel is the moment staleness is visible, so re-fetch right then — the backstop for
  // a missed presence push and for changes made directly on the engine.
  useEffect(() => {
    if (isOpen) void refreshSynced();
  }, [isOpen, refreshSynced]);

  // One merged list. A synced account the user also added by hand shows ONCE, as their own local row —
  // keeping its edit/delete affordances rather than being replaced by a read-only twin.
  const rows = useMemo<AccountRow[]>(() => {
    const localLogins = new Set(accounts.map((a) => a.credentials.login));
    return [
      ...accounts.map((account) => ({ kind: "local" as const, account })),
      ...selectUnmatchedSynced(syncedItems, localLogins).map((synced) => ({
        kind: "synced" as const,
        synced,
      })),
    ];
  }, [accounts, syncedItems]);

  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) return rows;
    const query = searchQuery.toLowerCase();
    return rows.filter((row) =>
      (row.kind === "local"
        ? [row.account.credentials.login, row.account.companyName, row.account.serverName]
        : [
            String(row.synced.login),
            row.synced.name ?? "",
            row.synced.gateway.companyName,
            row.synced.gateway.serverName,
          ]
      ).some((field) => field.toLowerCase().includes(query)),
    );
  }, [rows, searchQuery]);

  const handleSelectSynced = async (login: number) => {
    try {
      await connectSynced(login);
      onOpenChange(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not connect that account.");
    }
  };

  const handleSelectAccount = (accountId: string) => {
    if (accountId === activeAccountId && isConnected) {
      setCurrentView("account-details");
      return;
    }

    setActiveAccountId(accountId);
    onOpenChange(false);
  };

  const handleDeleteAccount = (accountId: string) => {
    deleteAccount(accountId);
    setShowDeleteConfirm(null);
  };

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const handleAddAccount = () => {
    setCurrentView("add-account");
  };

  const handleEditAccount = (accountId: string) => {
    setEditAccountId(accountId);
    setCurrentView("edit-account");
  };

  const editAccount = accounts.find((a) => a.id === editAccountId) ?? null;

  const handleBackToAccounts = () => {
    setCurrentView("accounts");
  };

  /**
   * A trading account from the user's Vybe Trader profile that isn't saved on this device. Selecting it
   * connects it; there is deliberately no edit or delete, because its name, server and credentials are
   * owned by the client portal — changing them here would mean writing back to the broker back-office.
   */
  const renderSyncedRow = (synced: SyncedAccountView) => {
    const isConnecting = connectingLogin === synced.login;
    const detail = [synced.mode, synced.currency].filter(Boolean).join(" · ");

    return (
      <Card key={`synced-${synced.login}`} className="transition-all hover:shadow-sm">
        <CardContent className="p-0">
          <div className="flex items-center gap-3 p-2.5">
            <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center">
              <BrokerLogo
                companyName={synced.gateway.companyName}
                logoUrl={gatewayLogoUrl(synced.gateway)}
              />
            </div>

            <Button
              variant="ghost"
              disabled={isConnecting}
              onClick={() => void handleSelectSynced(synced.login)}
              className="flex-1 h-auto p-0 justify-start hover:bg-transparent min-w-0"
            >
              <div className="text-left w-full min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="font-medium text-sm truncate">{synced.login}</span>
                  <Badge
                    variant="secondary"
                    className="text-[10px] px-1.5 py-0 h-4 flex-shrink-0 leading-none gap-1"
                  >
                    <ShieldCheck className="h-2.5 w-2.5" />
                    VYBE
                  </Badge>
                </div>
                <div className="text-xs text-muted-foreground truncate leading-tight">
                  {synced.gateway.companyName}
                </div>
                <div className="text-[10px] text-muted-foreground/70 truncate leading-tight">
                  {synced.gateway.serverName}
                  {detail && ` • ${detail}`}
                  {synced.balance && ` • ${synced.balance}`}
                </div>
              </div>
            </Button>

            {/* Read-only by design — the affordances a local row has (edit / delete) are absent, not disabled,
                so the panel never implies an action the client portal owns. */}
            <div className="flex items-center gap-0.5 flex-shrink-0">
              {isConnecting ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground mr-1.5" />
              ) : (
                <span className="text-[10px] text-muted-foreground/60 mr-1.5">Synced</span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderAccountsList = () => (
    <div className="flex flex-col h-full">
      <div className="px-3 py-3.5 border-b border-border">
        <div className="flex items-center justify-between gap-2 mb-2.5 pr-8">
          <div className="space-y-0.5 min-w-0">
            <h2 className="text-sm font-semibold">Trading Accounts</h2>
            <p className="text-xs text-muted-foreground truncate">
              Manage and switch between your accounts • {rows.length} total
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="flex items-center gap-1.5 h-7 text-xs px-2.5 shrink-0"
            onClick={handleAddAccount}
          >
            <PlusIcon className="h-3 w-3" />
            Add
          </Button>
        </div>
        
        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-3 w-3 text-muted-foreground" />
          <Input
            placeholder="Search by login, company or server..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8 h-7 text-xs border-muted"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-2.5">
        <div className="space-y-1.5">
          {/* The sync answered but its trading server didn't. Say so rather than quietly showing fewer
              accounts than the user has — local rows below are unaffected. */}
          {syncedStatus === "unavailable" && syncedError && (
            <Alert className="mb-2 border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/40">
              <AlertDescription className="text-[11px] leading-snug">
                {syncedError} Your saved accounts are still available.
              </AlertDescription>
            </Alert>
          )}

          {rows.length === 0 && syncedStatus === "loading" ? (
            <div className="flex flex-col items-center justify-center py-10">
              <Loader2 className="h-6 w-6 text-muted-foreground/60 animate-spin mb-2" />
              <p className="text-xs text-muted-foreground">Loading your accounts…</p>
            </div>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10">
              <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-lg border border-border bg-muted/30">
                <VybeLogo className="h-9 w-9" />
              </div>
              <h3 className="text-sm font-medium text-foreground mb-1.5">
                No accounts yet
              </h3>
              <p className="text-muted-foreground text-xs text-center mb-4 max-w-xs">
                Connect your first trading account to start using VybeTrader
              </p>
              <Button 
                className="flex items-center gap-1.5 h-7 text-xs px-3"
                onClick={handleAddAccount}
              >
                <PlusIcon className="h-3.5 w-3.5" />
                Add Account
              </Button>
            </div>
          ) : filteredRows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8">
              <Search className="h-7 w-7 text-muted-foreground/50 mb-2" />
              <p className="text-xs text-muted-foreground text-center">
                No accounts found matching "{searchQuery}"
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              {filteredRows.map((row) => {
                if (row.kind === "synced") return renderSyncedRow(row.synced);

                const account = row.account;
                const isActiveAndConnected = account.id === activeAccountId && isConnected;

                return (
                  <Card 
                    key={account.id} 
                    className={`transition-all hover:shadow-sm ${
                      isActiveAndConnected ? 'ring-1 ring-green-500/30 bg-green-50/30 dark:bg-green-950/10' : ''
                    }`}
                  >
                    <CardContent className="p-0">
                      <div className="flex items-center gap-3 p-2.5">
                        <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center">
                          <BrokerLogo companyName={account.companyName} logoUrl={account.brokerLogoUrl} />
                        </div>

                        <Button
                          variant="ghost"
                          onClick={() => handleSelectAccount(account.id)}
                          className="flex-1 h-auto p-0 justify-start hover:bg-transparent min-w-0"
                        >
                          <div className="text-left w-full min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="font-medium text-sm truncate">
                                {account.credentials.login}
                              </span>
                              {isActiveAndConnected && (
                                <Badge className="bg-green-500 hover:bg-green-600 text-white text-[10px] px-1.5 py-0 h-4 flex-shrink-0 leading-none">
                                  ACTIVE
                                </Badge>
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground truncate leading-tight">
                              {account.companyName}
                            </div>
                            <div className="text-[10px] text-muted-foreground/70 truncate leading-tight">
                              {account.serverName} • Last: {formatDate(account.lastConnectedAt)}
                            </div>
                          </div>
                        </Button>

                        <div className="flex items-center gap-0.5 flex-shrink-0">
                          {isActiveAndConnected && (
                            <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse mr-1" />
                          )}

                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditAccount(account.id);
                            }}
                            className="h-6 w-6 flex-shrink-0 hover:bg-primary/10 hover:text-primary opacity-70 hover:opacity-100"
                            aria-label="View or edit account"
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>

                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowDeleteConfirm(account.id);
                            }}
                            className="h-6 w-6 flex-shrink-0 hover:bg-red-500/10 hover:text-red-400 opacity-70 hover:opacity-100"
                            aria-label="Delete account"
                          >
                            <TrashIcon className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>

                      {/* Delete Confirmation */}
                      {showDeleteConfirm === account.id && (
                        <div className="px-2.5 pb-2.5">
                          <Alert className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950">
                            <AlertDescription>
                              <p className="text-xs mb-2 font-medium">
                                Delete account {account.credentials.login}?
                              </p>
                              <p className="text-[10px] text-muted-foreground mb-2">
                                This action cannot be undone.
                              </p>
                              <div className="flex gap-2">
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => handleDeleteAccount(account.id)}
                                  className="flex-1 h-6 text-xs px-2"
                                >
                                  Delete
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setShowDeleteConfirm(null)}
                                  className="flex-1 h-6 text-xs px-2"
                                >
                                  Cancel
                                </Button>
                              </div>
                            </AlertDescription>
                          </Alert>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Footer Summary */}
      {rows.length > 0 && (
        <div className="px-3 py-2.5 border-t border-border">
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
              <span className="text-muted-foreground">
                {accounts.filter(acc => acc.id === activeAccountId && isConnected).length} connected
              </span>
            </div>
            <div className="flex items-center gap-2">
              {searchQuery && (
                <span className="text-muted-foreground">
                  {filteredRows.length} of {rows.length}
                </span>
              )}
              <Badge variant="secondary" className="font-medium text-[10px] h-4 px-1.5 leading-none">
                {rows.length} total
              </Badge>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetTrigger asChild>
        {trigger}
      </SheetTrigger>
      
      <SheetContent side="left" className="w-[400px] sm:w-[540px] flex flex-col overflow-hidden p-0">
        {/* Main View */}
        <div className={`absolute inset-0 transition-transform duration-300 ease-in-out ${
          currentView === "accounts" ? "translate-x-0" : "translate-x-full"
        } flex flex-col`}>
          {renderAccountsList()}
        </div>
        
        {/* Add Account View */}
        <div className={`absolute inset-0 transition-transform duration-300 ease-in-out ${
          currentView === "add-account" ? "translate-x-0" : "-translate-x-full"
        } flex flex-col`}>
          <AddAccountContent 
            onBack={handleBackToAccounts}
            onAccountAdded={() => {
              setCurrentView("accounts");
            }}
          />
        </div>

        {/* Account Details View */}
        <div className={`absolute inset-0 transition-transform duration-300 ease-in-out ${
          currentView === "account-details" ? "translate-x-0" : "translate-x-full"
        } flex flex-col`}>
          <AccountDetailsScreen
            embedded
            onBack={() => setCurrentView("accounts")}
          />
        </div>

        {/* View / Edit Account */}
        <div className={`absolute inset-0 transition-transform duration-300 ease-in-out ${
          currentView === "edit-account" ? "translate-x-0" : "translate-x-full"
        } flex flex-col`}>
          {editAccount && (
            <EditAccountContent
              key={editAccount.id}
              account={editAccount}
              onBack={() => setCurrentView("accounts")}
            />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}